import React from 'react'
import { BarChart3, FileText, Hash, Layers, Clock } from 'lucide-react'
import { getJsonStats, getJsonSize } from '../utils/jsonUtils'

const StatsPanel = ({ jsonData, jsonString }) => {
  const stats = jsonData ? getJsonStats(jsonData) : null
  const size = jsonString ? getJsonSize(jsonString) : 0

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getTypeColor = (type) => {
    const colors = {
      string: '#28a745',
      number: '#007bff',
      boolean: '#ffc107',
      object: '#6f42c1',
      array: '#fd7e14',
      null: '#6c757d'
    }
    return colors[type] || '#6c757d'
  }

  if (!stats) {
    return (
      <div className="panel">
        <div className="panel-header">
          <span>JSON Statistics</span>
        </div>
        <div className="panel-content" style={{ padding: '20px', textAlign: 'center', color: 'var(--text-color)', opacity: 0.6 }}>
          <BarChart3 size={48} style={{ marginBottom: '16px' }} />
          <p>No JSON data to analyze</p>
        </div>
      </div>
    )
  }

  return (
    <div className="panel">
      <div className="panel-header">
        <span>JSON Statistics</span>
        <BarChart3 size={16} />
      </div>
      
      <div className="panel-content" style={{ padding: '16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '16px', marginBottom: '20px' }}>
          <div style={{ textAlign: 'center', padding: '12px', backgroundColor: 'var(--secondary-bg)', borderRadius: '8px' }}>
            <FileText size={24} style={{ color: 'var(--primary-color)', marginBottom: '8px' }} />
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--text-color)' }}>
              {stats.keys}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-color)', opacity: 0.7 }}>
              Total Keys
            </div>
          </div>
          
          <div style={{ textAlign: 'center', padding: '12px', backgroundColor: 'var(--secondary-bg)', borderRadius: '8px' }}>
            <Layers size={24} style={{ color: 'var(--success-color)', marginBottom: '8px' }} />
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--text-color)' }}>
              {stats.depth}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-color)', opacity: 0.7 }}>
              Max Depth
            </div>
          </div>
          
          <div style={{ textAlign: 'center', padding: '12px', backgroundColor: 'var(--secondary-bg)', borderRadius: '8px' }}>
            <Hash size={24} style={{ color: 'var(--warning-color)', marginBottom: '8px' }} />
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--text-color)' }}>
              {formatBytes(size)}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-color)', opacity: 0.7 }}>
              Size
            </div>
          </div>
        </div>

        <div>
          <h4 style={{ marginBottom: '12px', color: 'var(--text-color)', fontSize: '14px', fontWeight: '600' }}>
            Data Types Distribution
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {Object.entries(stats.types).map(([type, count]) => (
              <div key={type} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: getTypeColor(type)
                    }}
                  />
                  <span style={{ fontSize: '13px', color: 'var(--text-color)', textTransform: 'capitalize' }}>
                    {type}
                  </span>
                </div>
                <span style={{ fontSize: '13px', color: 'var(--text-color)', fontWeight: '500' }}>
                  {count}
                </span>
              </div>
            ))}
          </div>
        </div>

        {stats.size > 0 && (
          <div style={{ marginTop: '16px', padding: '12px', backgroundColor: 'var(--secondary-bg)', borderRadius: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Clock size={14} />
              <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-color)' }}>
                Performance Info
              </span>
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-color)', opacity: 0.8 }}>
              <div>Raw size: {stats.size} characters</div>
              <div>Estimated parse time: &lt; 1ms</div>
              <div>Memory usage: ~{formatBytes(stats.size * 2)}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default StatsPanel
